// frontend/src/KlaviyoProfileCleanup/MappingTabs.jsx
import React, { useState } from 'react';
import { FormLayout, Select, TextField } from '@shopify/polaris';
import { Modal, useDebounced } from '../ItAssets/common';
import { kpcApi } from './apiClient';
import { Alert, Confirm, Pill, useKpc, useList, when, ws } from './common';

const KIND_LABEL = { typo: 'Typo', whitespace: 'Whitespace', closed_store: 'Closed store', teams_location: 'Teams location', online: 'Online', other: 'Other' };
const SOURCE_LABEL = { seed_csv: 'seed · CSV', seed_code: 'seed · pickup script', seed_tuo: 'seed · TUO export', approved: 'approved', manual: 'added here' };

function RowActions({ row, onEdit, onToggle, onLock, onDelete }) {
  return (
    <td className="r uh-ia-nowrap" onClick={(e) => e.stopPropagation()}>
      <button type="button" className="uh-kpc-lock" title={row.locked ? 'Unlock' : 'Lock'} aria-label={row.locked ? 'Unlock' : 'Lock'} onClick={() => onLock(row)}>{row.locked ? '🔒' : '🔓'}</button>
      <button type="button" className="uh-btn slim" disabled={row.locked} onClick={() => onEdit(row)}>Edit</button>{' '}
      <button type="button" className="uh-btn slim" disabled={row.locked} onClick={() => onToggle(row)}>{row.enabled ? 'Disable' : 'Enable'}</button>{' '}
      <button type="button" className="uh-btn slim" disabled={row.locked} onClick={() => onDelete(row)}>Delete</button>
    </td>
  );
}

// Client-side sort: the rules and clubs lists come back whole (no paging), so sorting is a reorder of what is shown.
const SORT_VALUE = { enabled: (r) => (r.enabled ? 1 : 0), edited: (r) => r.updated_at || '' };
function compareRows(a, b, key, dir) {
  const get = SORT_VALUE[key] || ((r) => r[key]);
  const va = get(a), vb = get(b);
  const na = va === null || va === undefined || va === '', nb = vb === null || vb === undefined || vb === '';
  if (na || nb) return na && nb ? 0 : na ? 1 : -1;                                       // blanks last, whatever the direction
  const c = typeof va === 'number' && typeof vb === 'number' ? va - vb : String(va).localeCompare(String(vb), undefined, { sensitivity: 'base', numeric: true });
  return dir === 'desc' ? -c : c;
}
function SortTh({ k, label, num, sort, onSort }) {
  const on = sort.key === k;
  return (
    <th className={num ? 'r' : ''} aria-sort={on ? (sort.dir === 'desc' ? 'descending' : 'ascending') : 'none'}>
      <button type="button" className="uh-ia-sort" onClick={() => onSort(k, !!num)}>{label}<span className={on ? 'on' : ''}>{on ? (sort.dir === 'desc' ? '▼' : '▲') : '↕'}</span></button>
    </th>
  );
}

/** One editable, lockable table. `spec` says what the rows are. */
function MappingTable({ spec }) {
  const { notify } = useKpc();
  const [q, setQ] = useState('');
  const [filter, setFilter] = useState('');
  const [showOff, setShowOff] = useState(true);
  const dq = useDebounced(q);
  const { data, error, reload } = useList(() => spec.list({ q: dq, [spec.filterKey]: filter }), [dq, filter]);
  const [dialog, setDialog] = useState(null);           // { row } edit, { add: true }, { del: row }
  const [err, setErr] = useState('');
  const [sort, setSort] = useState({ key: '', dir: 'asc' });   // '' = the server's order (by name)
  const onSort = (key, descFirst) => setSort((s) => (s.key === key ? { key, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: descFirst ? 'desc' : 'asc' }));
  const shown = data ? data[spec.key].filter((r) => showOff || r.enabled) : [];
  const rows = sort.key ? [...shown].sort((a, b) => compareRows(a, b, sort.key, sort.dir)) : shown;
  const openDialog = (d) => { setErr(''); setDialog(d); };
  const act = async (fn, msg) => { setErr(''); try { await fn(); notify(msg); reload(); setDialog(null); } catch (e) { setErr(e.message); } };
  return (
    <div className="uh-card">
      <div className="uh-ia-tbar">
        <input className="uh-inp" style={{ minWidth: 240 }} placeholder={spec.searchHint} value={q} onChange={(e) => setQ(e.target.value)} aria-label="Search" />
        <select className="uh-select" aria-label={spec.filterLabel} value={filter} onChange={(e) => setFilter(e.target.value)}>
          <option value="">{spec.filterAll}</option>{(data ? spec.filterOptions(data) : []).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        <label className="uh-ia-check"><input type="checkbox" checked={showOff} onChange={(e) => setShowOff(e.target.checked)} /> Show disabled</label>
        <span className="uh-sp" /><span className="uh-muted">{rows.length} {spec.noun}</span>
        <button type="button" className="uh-btn slim primary" onClick={() => openDialog({ add: true })}>{spec.addLabel}</button>
      </div>
      <Alert>{error || err}</Alert>
      {spec.extra && spec.extra(data)}
      <div className="uh-kpc-scroll"><table className="uh-tbl">
        <thead><tr>{spec.columns.map(([k, label, num]) => <SortTh key={k} k={k} label={label} num={num} sort={sort} onSort={onSort} />)}<SortTh k="enabled" label="Enabled" sort={sort} onSort={onSort} />{!spec.noEdited && <SortTh k="edited" label="Last edited" sort={sort} onSort={onSort} />}<th className="r" /></tr></thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id} className={r.locked ? 'uh-kpc-locked' : ''}>
              {spec.columns.map(([k, , num, render]) => <td key={k} className={num ? 'r uh-num' : ''}>{render ? render(r) : r[k]}</td>)}
              <td>{r.enabled ? 'Yes' : <Pill tone="bad">Off</Pill>}{r.locked && <span className="uh-faint" title="Locked: unlock before changing"> · locked</span>}</td>
              {!spec.noEdited && <td className="uh-muted uh-ia-nowrap">{SOURCE_LABEL[r.source] || r.source}{r.updated_by !== 'seed' && ` · ${r.updated_by} · ${when(r.updated_at)}`}</td>}
              <RowActions row={r} onEdit={(row) => openDialog({ row })}
                onToggle={(row) => act(() => spec.edit(row.id, { enabled: !row.enabled }), row.enabled ? 'Disabled' : 'Enabled')}
                onLock={(row) => act(() => spec.edit(row.id, { locked: !row.locked }), row.locked ? 'Unlocked' : 'Locked')}
                onDelete={(row) => openDialog({ del: row })} />
            </tr>))}
          {data && !rows.length && <tr><td colSpan={spec.columns.length + (spec.noEdited ? 2 : 3)}><div className="uh-empty">Nothing matches.</div></td></tr>}
          {!data && !error && <tr><td colSpan={spec.columns.length + (spec.noEdited ? 2 : 3)}><div className="uh-empty">Loading…</div></td></tr>}
        </tbody></table></div>
      <div className="uh-tfoot"><span>{spec.footer}</span></div>
      {dialog && (dialog.add || dialog.row) && <spec.Dialog row={dialog.row} data={data} onClose={() => openDialog(null)} onSave={(body) => act(() => (dialog.row ? spec.edit(dialog.row.id, body) : spec.add(body)), dialog.row ? 'Saved' : 'Added')} error={err} />}
      {dialog && dialog.del && <Confirm title={spec.deleteTitle(dialog.del)} text="This cannot be undone. The nightly run stops applying it tonight." yes="Delete" danger onYes={() => act(() => spec.remove(dialog.del.id), 'Deleted')} onNo={() => openDialog(null)} error={err} />}
    </div>
  );
}

function RuleDialog({ row, onClose, onSave, error }) {
  const [f, setF] = useState({ from_value: row ? row.from_value : '', to_value: row ? row.to_value : '', kind: row ? row.kind : 'typo', note: row ? row.note : '' });
  const set = (k) => (v) => setF((s) => ({ ...s, [k]: v }));
  const ready = f.from_value.trim() && f.to_value.trim();
  return (
    <Modal title={row ? 'Edit rule' : 'Add a rule'} subtitle="Matching is exact, including leading and trailing spaces." onClose={onClose} focusFirst
      footer={<><button type="button" className="uh-btn" onClick={onClose}>Cancel</button><button type="button" className="uh-btn primary" disabled={!ready} onClick={() => onSave(row ? { to_value: f.to_value, kind: f.kind, note: f.note } : f)}>{row ? 'Save' : 'Add rule'}</button></>}>
      <Alert>{error}</Alert>
      <FormLayout>
        <TextField label="When Local Store is exactly" value={f.from_value} onChange={set('from_value')} autoComplete="off" disabled={!!row} helpText={row ? 'The matched value cannot change; add a new rule instead.' : 'Paste the value as Klaviyo stores it.'} requiredIndicator />
        <TextField label="Set it to" value={f.to_value} onChange={set('to_value')} autoComplete="off" requiredIndicator />
        <Select label="Kind" options={Object.entries(KIND_LABEL).map(([value, label]) => ({ value, label }))} value={f.kind} onChange={set('kind')} />
        <TextField label="Note" value={f.note} onChange={set('note')} autoComplete="off" multiline={2} />
      </FormLayout>
    </Modal>
  );
}

function ClubDialog({ row, data, onClose, onSave, error }) {
  const stores = data ? data.stores || [] : [];
  const [club, setClub] = useState(row ? row.club : '');
  const [note, setNote] = useState(row ? row.note : '');
  const [pick, setPick] = useState(row && stores.includes(row.store) ? row.store : '__other');
  const [other, setOther] = useState(row && !stores.includes(row.store) ? row.store : '');
  const store = pick === '__other' ? other : pick;
  const ready = club.trim() && store.trim();
  return (
    <Modal title={row ? 'Edit club' : 'Add a club'} subtitle="The club is matched exactly as it appears on the profile, season suffix included." onClose={onClose} focusFirst
      footer={<><button type="button" className="uh-btn" onClick={onClose}>Cancel</button><button type="button" className="uh-btn primary" disabled={!ready} onClick={() => onSave(row ? { store, note } : { club, store, note })}>{row ? 'Save' : 'Add club'}</button></>}>
      <Alert>{error}</Alert>
      <FormLayout>
        <TextField label="Club" value={club} onChange={setClub} autoComplete="off" disabled={!!row} placeholder="Manhattan Kickers 2026-2027" requiredIndicator />
        <Select label="Local Store" options={[...stores.map((s) => ({ value: s, label: s })), { value: '__other', label: 'Other store…' }]} value={pick} onChange={setPick} />
        {pick === '__other' && <TextField label="Store name" value={other} onChange={setOther} autoComplete="off" placeholder="Soccer Post Manhattan" requiredIndicator />}
        <TextField label="Note" value={note} onChange={setNote} autoComplete="off" multiline={2} />
      </FormLayout>
    </Modal>
  );
}

function Tester() {
  const [v, setV] = useState('');
  const [out, setOut] = useState(null);
  const go = async () => { try { setOut(await kpcApi.testValue(v)); } catch (e) { setOut({ error: e.message }); } };
  return (
    <div className="uh-card-b">
      <div className="uh-ia-tbar" style={{ border: 0, padding: 0 }}>
        <input className="uh-inp" style={{ minWidth: 280 }} placeholder="Paste a Local Store value" value={v} onChange={(e) => setV(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') go(); }} aria-label="Value to test" />
        <button type="button" className="uh-btn slim" onClick={go}>What happens?</button>
      </div>
      {out && (out.error ? <Alert>{out.error}</Alert> : (
        <div className="uh-kpc-chain" style={{ marginTop: 10 }}>
          <span className="step">{ws(out.value)}</span>
          {out.steps.map((s) => <React.Fragment key={s.rule}><span className="arrow">→</span><span className="step">rule <b>#{s.rule}</b></span><span className="arrow">→</span><span className="step">{ws(s.to_value)}</span></React.Fragment>)}
          {!out.steps.length && <><span className="arrow">→</span><span className="step">no rule matches; left as is</span></>}
        </div>))}
    </div>
  );
}

const RULES = {
  key: 'rules', noun: 'rules', searchHint: 'Search a value or a store', filterKey: 'kind', filterLabel: 'Kind', filterAll: 'All kinds',
  filterOptions: () => Object.entries(KIND_LABEL), addLabel: 'Add rule',
  list: (p) => kpcApi.rules(p), add: (b) => kpcApi.addRule(b), edit: (id, b) => kpcApi.editRule(id, b), remove: (id) => kpcApi.deleteRule(id),
  columns: [['id', '#', 0, (r) => <span className="uh-muted">{r.id}</span>], ['from_value', 'When Local Store is exactly', 0, (r) => ws(r.from_value)], ['to_value', 'Set it to', 0, (r) => ws(r.to_value)],
    ['kind', 'Kind', 0, (r) => <span className="uh-ia-tag">{KIND_LABEL[r.kind] || r.kind}</span>], ['fired_last_night', 'Fired last run', 1]],
  Dialog: RuleDialog, deleteTitle: (r) => `Delete the rule “${r.from_value}”?`, extra: () => <Tester />, noEdited: true,
  footer: 'Shop codes from Lightspeed (MD Towson) and Shopify pickup names (Teams Dublin) are converted to “Soccer Post City” first, then the rules run. A locked rule cannot be changed until it is unlocked. Every edit is in the activity log.',
};

const CLUBS = {
  key: 'clubs', noun: 'clubs', searchHint: 'Search a club or a store', filterKey: 'store', filterLabel: 'Store', filterAll: 'All stores',
  filterOptions: (data) => (data.stores || []).map((s) => [s, s]), addLabel: 'Add club',
  list: (p) => kpcApi.clubs(p), add: (b) => kpcApi.addClub(b), edit: (id, b) => kpcApi.editClub(id, b), remove: (id) => kpcApi.deleteClub(id),
  columns: [['club', 'Club (as on the profile)'], ['store', 'Local Store'], ['profiles_now', 'Still invalid now', 1]],
  Dialog: ClubDialog, deleteTitle: (r) => `Delete the club “${r.club}”?`, noEdited: true,
  footer: 'A profile whose club matches exactly and whose Local Store is still bad gets this store in the order-history pass, after POS orders and pickups. Approving a suggestion on the Clubs Needing Mapping tab adds a row here.',
};

export const RulesTab = () => <MappingTable spec={RULES} />;
export const ClubsTab = () => <MappingTable spec={CLUBS} />;
